import i18next from "i18next";
import { toast } from "react-toastify";

import { useTranslate } from "@/hooks";
import { toShown } from "@/lib/wallet/show";
import { AffSubIds } from "@/types/auth";

import {
  AFFILIATE_CODE_STORAGE_KEY,
  ATTRIBUTION_CODE_TTL_MS,
  REFERRAL_CODE_STORAGE_KEY,
} from "./data";
import { StoredAffiliateCode, StoredReferralCode } from "./type";

export * from "./number";
export * from "./scroll";

export const resolveTranslatedLabel = (
  translate: ReturnType<typeof useTranslate>,
  name: string,
  keyPrefix: string,
): string => {
  const direct = String(translate(name));
  if (direct !== name) return direct;

  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const key = `${keyPrefix}.${slug}`;
  const viaSlug = String(translate(key));
  return viaSlug !== key ? viaSlug : name;
};

export const localStorageGetItem = (key: string, defaultValue = "") => {
  return localStorage.getItem(key) || defaultValue;
};

export const captureReferralCodeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  // Any link carrying ?ref= counts, not only the ones that also open the sign-up dialog.
  const ref = params.get("ref")?.trim();
  if (!ref || !/^[A-Za-z0-9_-]{1,64}$/.test(ref)) return;

  if (getStoredReferralCode()) return;

  try {
    const value: StoredReferralCode = { code: ref, capturedAt: Date.now() };
    localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.warn("Failed to store referral code", error);
  }
};

export const getStoredReferralCode = (): string | undefined => {
  try {
    const raw = localStorage.getItem(REFERRAL_CODE_STORAGE_KEY);
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as Partial<StoredReferralCode>;
    if (!stored.code || !stored.capturedAt) return undefined;

    if (Date.now() - stored.capturedAt > ATTRIBUTION_CODE_TTL_MS) {
      localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
      return undefined;
    }

    return stored.code;
  } catch (error) {
    console.warn("Failed to read referral code", error);
    return undefined;
  }
};

export const clearStoredReferralCode = () => {
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear referral code", error);
  }
};

const AFF_SUB_ID_KEYS = ["sub1", "sub2", "sub3", "sub4", "sub5"] as const;

export const captureAffiliateCodeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const affCode = params.get("affcode");
  if (!affCode) return;

  if (getStoredAffiliateCode()) return;

  try {
    const subIds: AffSubIds = {};
    for (const key of AFF_SUB_ID_KEYS) {
      const value = params.get(key)?.trim().slice(0, 100);
      if (value) subIds[key] = value;
    }
    const value: StoredAffiliateCode = {
      affCode,
      clickId: params.get("clickid") || undefined,
      subIds: Object.keys(subIds).length ? subIds : undefined,
      capturedAt: Date.now(),
    };
    localStorage.setItem(AFFILIATE_CODE_STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    console.warn("Failed to store affiliate code", error);
  }
};

export const getStoredAffiliateCode = ():
  | { affCode: string; clickId?: string; subIds?: AffSubIds }
  | undefined => {
  try {
    const raw = localStorage.getItem(AFFILIATE_CODE_STORAGE_KEY);
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as Partial<StoredAffiliateCode>;
    if (!stored.affCode || !stored.capturedAt) return undefined;

    if (Date.now() - stored.capturedAt > ATTRIBUTION_CODE_TTL_MS) {
      localStorage.removeItem(AFFILIATE_CODE_STORAGE_KEY);
      return undefined;
    }

    return { affCode: stored.affCode, clickId: stored.clickId, subIds: stored.subIds };
  } catch (error) {
    console.warn("Failed to read affiliate code", error);
    return undefined;
  }
};

export const clearStoredAffiliateCode = () => {
  try {
    localStorage.removeItem(AFFILIATE_CODE_STORAGE_KEY);
  } catch (error) {
    console.warn("Failed to clear affiliate code", error);
  }
};

export const hexToRgba = (hex: string, alpha: number = 100): string => {
  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
};

export const handleError = (err: unknown, defaultMessage?: string) => {
  // Shaped by lib/axios as not worth telling the player about.
  if (err instanceof Error && (err as { silent?: boolean }).silent) return;

  const raw = err instanceof Error ? err.message : defaultMessage || i18next.t("error.unknown");
  // Socket games pass server codes (e.g. country_restricted) straight through.
  const message =
    /^[a-z0-9_]+$/.test(raw) && i18next.exists(`error.${raw}`) ? i18next.t(`error.${raw}`) : raw;
  const toastOptions = { hideProgressBar: true, toastId: message };

  if (!toast.isActive(message)) {
    toast.error(message, toastOptions);
  } else {
    toast.update(message, { ...toastOptions, render: message });
  }
};

export const formatBalance = (amount: number, currency: string): string => {
  // Wallets hold USD; the player sees it in their show currency (lib/wallet/show).
  const shown = toShown(amount, currency || "USD");
  amount = shown.amount;
  const code = shown.currency;
  // The active language decides separators: 1,234.56 in en, 1.234,56 in es-AR.
  const locale = i18next.language || "en";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString(locale, { maximumFractionDigits: 2 })} ${code}`;
  }
};

export const formatCapitalCaseName = (str: string) => {
  return str
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
